export type ShipId =
  | 'fighter'
  | 'robotai'
  | 'juggernaut'
  | 'wraith'
  | 'corsair'
  | 'lancer'
  | 'skimmer';

export interface ShipDefinition {
  id: ShipId;
  displayName: string;
  /** Total hit points */
  armor: number;
  /** Base max speed cap, pixels/sec */
  speed: number;
  /** Thrust acceleration, pixels/sec² */
  thrust: number;
  /** Whether this ship has physics damping instead of frictionless drift */
  hasDamping: boolean;
  /** Energy pool capacity */
  energy: number;
  /** Energy regen per second */
  recharge: number;
  /** Energy drained per second while thrusting (brake costs one third of this) */
  thrustDrain: number;
  /** Physics mass (dimensionless, relative). Heavier ships absorb more impulse per unit and deal more damage to lighter ships. */
  mass: number;
  /** Collision circle radius in world pixels */
  radius: number;
  /** Shield pool capacity — absorbs weapon/collision damage before armor takes hits */
  shield: number;
  /** Shield regeneration rate in HP/s after recharge delay expires; costs energy 1:1 */
  shieldRecharge: number;
  /** Seconds of no-damage required before shields begin regenerating */
  shieldRechargeDelay: number;
  /** Hex colour used for the in-world shield glow and hit flash */
  shieldColor: number;
  /** Max damage dealt at ground-zero of this ship's death explosion (falls off linearly) */
  explosionDamage: number;
  /** Rotation speed in radians/sec — more nimble ships turn faster */
  rotationSpeed: number;
}

export const SHIP_DEFINITIONS: Record<ShipId, ShipDefinition> = {
  fighter: {
    id: 'fighter',
    displayName: 'The Fighter',
    armor: 80,
    speed: 600,
    thrust: 250,
    hasDamping: false,
    energy: 200,
    recharge: 26,
    thrustDrain: 28,
    mass: 1.0,
    radius: 32,
    shield: 55,
    shieldRecharge: 9,
    shieldRechargeDelay: 3,
    shieldColor: 0x4499ff,
    explosionDamage: 28,
    rotationSpeed: 4.2,
  },
  robotai: {
    id: 'robotai',
    displayName: 'The UFO',
    armor: 65,
    speed: 470,
    thrust: 300,
    hasDamping: false,
    energy: 220,
    recharge: 32,
    thrustDrain: 22,
    mass: 1.25,
    radius: 28,
    shield: 60,
    shieldRecharge: 9,
    shieldRechargeDelay: 3,
    shieldColor: 0xff4422,
    explosionDamage: 24,
    rotationSpeed: 3.8,
  },
  juggernaut: {
    id: 'juggernaut',
    displayName: 'The Tank',
    armor: 115,
    speed: 350,
    thrust: 155,
    hasDamping: false,
    energy: 320,
    recharge: 26,
    thrustDrain: 34,
    mass: 3.0,
    radius: 40,
    shield: 80,
    shieldRecharge: 7.5,
    shieldRechargeDelay: 3.5,
    shieldColor: 0xff8800,
    explosionDamage: 40,
    rotationSpeed: 2.7,
  },
  wraith: {
    id: 'wraith',
    displayName: 'The Wraith',
    armor: 70,
    speed: 720,
    thrust: 340,
    hasDamping: false,
    energy: 170,
    recharge: 28,
    thrustDrain: 32,
    mass: 0.70,
    radius: 25,
    shield: 55,
    shieldRecharge: 10,
    shieldRechargeDelay: 2,
    shieldColor: 0x00ff88,
    explosionDamage: 18,
    rotationSpeed: 5.2,
  },
  corsair: {
    id: 'corsair',
    displayName: 'The Corsair',
    armor: 105,
    speed: 500,
    thrust: 280,
    hasDamping: false,
    energy: 210,
    recharge: 25,
    thrustDrain: 30,
    mass: 1.65,
    radius: 34,
    shield: 45,
    shieldRecharge: 7,
    shieldRechargeDelay: 3,
    shieldColor: 0xff3355,
    explosionDamage: 32,
    rotationSpeed: 3.9,
  },
  lancer: {
    id: 'lancer',
    displayName: 'The Lancer',
    armor: 85,
    speed: 410,
    thrust: 190,
    hasDamping: false,
    energy: 240,
    recharge: 25,
    thrustDrain: 27,
    mass: 1.40,
    radius: 31,
    shield: 55,
    shieldRecharge: 8,
    shieldRechargeDelay: 3,
    shieldColor: 0xbb66ff,
    explosionDamage: 30,
    rotationSpeed: 3.0,
  },
  skimmer: {
    id: 'skimmer',
    displayName: 'The Skimmer',
    armor: 65,
    speed: 400,
    thrust: 620,
    hasDamping: true,
    energy: 280,
    recharge: 34,
    thrustDrain: 30,
    mass: 1.80,
    radius: 35,
    shield: 100,
    shieldRecharge: 13,
    shieldRechargeDelay: 4,
    shieldColor: 0x33ddff,
    explosionDamage: 32,
    rotationSpeed: 3.4,
  },
};
